import { extendObservable, runInAction, autorun } from 'mobx';

import { CartController } from '../networking/controllers/CartController';
import { ProductsController } from '../networking/controllers/ProductsController';
import { Product } from '../models/Product';
import { SimpleProduct } from '../models/SimpleProduct';
import { Storage } from '../helpers/Storage';
import { CART_ITEM_STATES, calculateCartItemAmount, unIncludeDebitCartTax } from '../helpers/CartHelper';
import { Logger } from '../helpers/Logger';
import { NumberHelper } from '../helpers/NumberHelper';
import { SessionStore } from './SessionStore';
import { routeNaming, goToPage } from '../routes';
import { constants } from '../config';

/*
  This store handles all session-related logic.
*/

const CART_KEY = 'CartStore-cart';
const FAVORITES_KEY = 'CartStore-favorites';

const MAX_LIST_RETRIES = 3;
const POP_UP_TIMEOUT = 4000;
const DEBIT_CARD_PRODUCT_ID = constants.productIdCreditCardTax;

const LIST_TYPES = {
  CART: 'cart',
  FAVORITES: 'favorites',
};

class CartStoreClass {
  static clearStorage() {
    Storage.remove(CART_KEY);
    Storage.remove(FAVORITES_KEY);
  }

  constructor() {
    extendObservable(this, {
      cart: null,
      favorites: null,
      lastDeletions: [],
      isFetchingCart: false,
      isFetchingFavorites: false,
      operationInProgress: false,
      lastProductUpdated: null,
      mergingInProgress: true,
      get cartTotalPrice() {
        return this.cart ? this.cart.reduce((acc, item) => {
          const productPrice = item.product.financing.price * item.amount;
          const servicePrice = item.serviceAmount ?
            item.product.serviceProduct.financing.price * item.serviceAmount :
            0;
          return acc + productPrice + servicePrice;
        }, 0) : 0;
      },
      get cartTotalPriceWithoutDebitCardTax() {
        return this.cart ? unIncludeDebitCartTax(this.cart).reduce((acc, item) => {
          const productPrice = item.product.financing.price * item.amount;
          const servicePrice = item.serviceAmount ?
            item.product.serviceProduct.financing.price * item.serviceAmount :
            0;
          return acc + productPrice + servicePrice;
        }, 0) : 0;
      },
      get cartStatusIsValid() {
        return this.cart ? this.cart.reduce((acc, item) => (
          acc && item.state === CART_ITEM_STATES.NO_ISSUES
        ), true) : 0;
      },
      get cartTotalPriceString() {
        return NumberHelper.localizeNumber(this.cartTotalPrice);
      },
      get cartTotalPriceWithoutDebitCardTaxString() {
        return NumberHelper.localizeNumber(this.cartTotalPriceWithoutDebitCardTax);
      },
      get cartTotalItems() {
        if (!this.cart) {
          return 0;
        }
        return calculateCartItemAmount(this.cart);
      },
    });
    this.productDebitCardTax = null;
    this.firstLoad = true;
    this.listTypes = LIST_TYPES;
    this.cartRetries = 0;
    this.favoriteRetries = 0;
    this.lastTimeout = 0;
    this._turnOffPopUp = this._turnOffPopUp.bind(this);
    this._handleCartMerging = this._handleCartMerging.bind(this);
    // This store depends strongly on the state of the session.
    // This autorun listens to changes on the SessionStore and
    // updates this store's data accordingly.
    // Even though this is not the best way to implement this, it's
    // fairly easy to do.
    // Read more here: https://github.com/mobxjs/mobx/issues/715
    autorun(() => {
      this._onSessionStateChange(SessionStore.loggedIn);
    });
  }

  static createProduct(data) {
    /*
      HACK: It's neccessary to instantiate the object correctly, so we need to distinguish
      SimpleProducts from Products. We should think of a better way to do this.
    */
    return data.images ? new Product(data) : new SimpleProduct(data);
  }

  static fetchCartFromStorage() {
    return (Storage.secureGet(CART_KEY) || []).map(item => ({
      amount: item.amount,
      product: CartStoreClass.createProduct(item.product),
      serviceAmount: item.serviceAmount,
      state: item.state,
    }));
  }

  static fetchFavoritesFromStorage() {
    return (Storage.secureGet(FAVORITES_KEY) || []).map(CartStoreClass.createProduct);
  }

  static mergeCart(storageCart, backendCart) {
    const resultCart = backendCart;
    storageCart.forEach((storageItem) => {
      const itemInCart = resultCart.find(item => item.product.id === storageItem.product.id);
      const itemExistsInCart = !!itemInCart;
      const itemHasService = (itemExistsInCart && itemInCart.serviceAmount > 0) ||
        storageItem.serviceAmount > 0;
      if (itemExistsInCart && !itemHasService) {
        const newAmount = itemInCart.amount + storageItem.amount;
        itemInCart.amount = newAmount > itemInCart.product.maxAmountToSell ?
          itemInCart.product.maxAmountToSell : newAmount;
      } else if (itemExistsInCart && itemHasService) {
        // Products with service are prioritised before products with no service
        const newAmountWithService = itemInCart.serviceAmount + storageItem.serviceAmount;
        if (newAmountWithService > itemInCart.product.maxAmountToSell) {
          itemInCart.amount = itemInCart.product.maxAmountToSell;
          itemInCart.serviceAmount = itemInCart.product.maxAmountToSell;
        } else {
          const newAmount = itemInCart.amount + storageItem.amount;
          itemInCart.amount = newAmount > itemInCart.product.maxAmountToSell ?
            itemInCart.product.maxAmountToSell : newAmount;
          itemInCart.serviceAmount = newAmountWithService;
        }
      } else {
        resultCart.push(storageItem);
      }
    });
    return resultCart;
  }

  _fetchListsFromStorage() {
    runInAction(() => {
      this.cart = CartStoreClass.fetchCartFromStorage();
      this.favorites = CartStoreClass.fetchFavoritesFromStorage();
    });
    Logger.log('Lists fetched from storage');
  }

  async _fecthDebitCardTax() {
    try {
      const product = await ProductsController.find(DEBIT_CARD_PRODUCT_ID);
      runInAction(() => {
        this.productDebitCardTax = product;
      });
    } catch (error) {
      Logger.warn(`Stopped trying to fetch favorites, last error was ${error.message}`);
      throw error;
    }
  }

  async _fetchFavoritesFromBackend() {
    try {
      const favorites = await CartController.getFavorites();
      this.favoriteRetries = 0;
      Logger.log('Favorites fetched from backend');
      return favorites;
    } catch (error) {
      if (this.favoriteRetries < MAX_LIST_RETRIES) {
        this.favoriteRetries += 1;
        Logger.warn(`Failed to fetch favorites, retried ${this.favoriteRetries} times, error was: ${error.message}`);
        return this._fetchFavoritesFromBackend();
      }
      Logger.warn(`Stopped trying to fetch favorites, last error was ${error.message}`);
      throw error;
    }
  }

  async _fetchCartFromBackend() {
    try {
      const cart = await CartController.getCart();
      this.cartRetries = 0;
      Logger.log('Cart fetched from backend');
      return cart;
    } catch (error) {
      if (error.status === 401) {
        SessionStore.logout();
        goToPage(routeNaming.LOGIN);
      }
      if (this.cartRetries < MAX_LIST_RETRIES) {
        this.cartRetries += 1;
        Logger.warn(`Failed to fetch cart, retried ${this.cartRetries} times, error was: ${error.message}`);
        return this._fetchCartFromBackend();
      }
      Logger.warn(`Stopped trying to fetch cart, last error was ${error.message}`);
      throw error;
    }
  }

  async _updateCartFromBackend() {
    try {
      runInAction(() => {
        this.isFetchingCart = true;
      });
      const cart = await this._fetchCartFromBackend();
      runInAction(() => {
        this.cart = cart;
      });
    } catch (error) {
      runInAction(() => {
        this.cart = null;
      });
    } finally {
      runInAction(() => {
        this.isFetchingCart = false;
      });
    }
  }

  async _updateFavoritesFromBackend() {
    try {
      runInAction(() => {
        this.isFetchingFavorites = true;
      });
      const favorites = await this._fetchFavoritesFromBackend();
      runInAction(() => {
        this.favorites = favorites;
      });
    } catch (error) {
      runInAction(() => {
        this.favorites = null;
      });
    } finally {
      runInAction(() => {
        this.isFetchingFavorites = false;
      });
    }
  }

  async _fetchListsFromBackend() {
    return Promise.all([
      this._updateCartFromBackend(),
      this._updateFavoritesFromBackend(),
      this._fecthDebitCardTax(),
    ]).then(() => {
      this.deleteProductFromCart(DEBIT_CARD_PRODUCT_ID);
    });
  }

  _clearCart() {
    runInAction(() => {
      this.cart = [];
      this.favorites = [];
    });
    CartStoreClass.clearStorage();
  }

  /*
    Cart merging works as follows: when the user has just logged in,
    the cart must be loaded from the storage. If the storage has something in it,
    then the cart from the storage and the cart from the backend are merged to
    create a new cart with the products of both. If the cart from the storage
    has nothing, then the backend cart must be loaded.
  */
  async _handleCartMerging() {
    runInAction(() => {
      this.mergingInProgress = true;
    });
    const storageCart = CartStoreClass.fetchCartFromStorage();
    if (storageCart && storageCart.length) {
      const backendCart = await this._fetchCartFromBackend();
      const resultCart = CartStoreClass.mergeCart(storageCart, backendCart);
      runInAction(() => {
        this.cart = resultCart;
      });
      await this._postNewCartOnBackend(this.cart);
    }
    await this._fetchListsFromBackend();
    CartStoreClass.clearStorage();
    runInAction(() => {
      this.mergingInProgress = false;
    });
  }

  /*
    Cart logic is fairly complicated.
      - If at any point the user logs out the cart must be flushed.
      - When loading the app, if the user is not logged in, load cart from storage.
      - When loading the app, if the user is logged, fetch from backend
  */
  _onSessionStateChange(loggedIn) {
    if (!loggedIn && this.firstLoad) {
      // The page has just loaded and the user is not logged in
      this._fetchListsFromStorage();
    } else if (loggedIn) {
      // NOTE: The purpose of this timeout is to break the execution context
      // of `this._handleCartMerging`. Since that function reads the cart *and* updates it
      // the autorun function causes itself to run again, creating an infinite loop.
      // Setting a timeout ensures the function runs outside of Mobx's autorun scope.
      window.setTimeout(this._handleCartMerging, 0);
    } else {
      this._clearCart();
      Logger.log('Cart Cleared');
    }
    this.firstLoad = false;
  }

  _updateLastProductUpdated(product, hasService) {
    runInAction(() => {
      // The store notifies the pop up when it has to be shown and
      // when it has to disappear
      this.lastProductUpdated = {
        product,
        hasService,
      };
    });
    // Timeouts have to be cleared if it didn't return yet
    if (this.lastTimeout) {
      clearTimeout(this.lastTimeout);
    }
    this.lastTimeout = setTimeout(this._turnOffPopUp, POP_UP_TIMEOUT);
  }

  async _postNewFavoritesOnBackend(newFavorites) {
    try {
      await CartController.updateFavorites(newFavorites);
      this.favoriteRetries = 0;
      return true;
    } catch (error) {
      if (this.favoriteRetries < MAX_LIST_RETRIES) {
        this.favoriteRetries += 1;
        Logger.warn(`Failed to update favorites, retried ${this.favoriteRetries} times, error was: ${error.message}`);
        return this._postNewFavoritesOnBackend(newFavorites);
      }
      Logger.warn(`Stopped trying to update favorites, last error was ${error.message}`);
      throw error;
    }
  }

  async _postNewCartOnBackend(newCart) {
    try {
      await CartController.updateCart(newCart);
      this.cartRetries = 0;
      return true;
    } catch (error) {
      if (this.cartRetries < MAX_LIST_RETRIES) {
        this.cartRetries += 1;
        Logger.warn(`Failed to update cart, retried ${this.cartRetries} times, error was: ${error.message}`);
        return this._postNewCartOnBackend(newCart);
      }
      Logger.warn(`Stopped trying to update cart, last error was ${error.message}`);
      throw error;
    }
  }

  /*
    Operations is an object that can have these keys:
      - remove: Array of product ids to remove from the list.
      - add: Array of items to add to the list
      - updateAmount: [Only for cart] Array of objects with `id` and `amount` keys.

    NOTE: please note that `remove` only accepts product ids, while
    `add` expects full items.
    NOTE: `items` must be compliant with the type of items the list holds. If
    you're adding to the cart, items must be an object with `amount` and `product` keys.
    NOTE: operations are executed in the order they were defined above.

    Operations are executed in a transaction-like environment. Updates to the observables are
    executed only when side-effects are successful (such as API queries or storage operations).
  */
  async _updateList(listType, operations, options = {}) {
    runInAction(() => {
      this.operationInProgress = true;
    });
    const updateLastProduct = options.updateLastProduct || false;
    const forceLastDeletion = options.forceLastDeletion || false;
    // Clone the list to make updates
    let newList = null;
    let initialLastDeletions = [...this.lastDeletions];
    if (listType === LIST_TYPES.CART) {
      // Before implementing operations, update the cart.
      if (!SessionStore.loggedIn) {
        newList = CartStoreClass.fetchCartFromStorage();
      } else {
        try {
          newList = await this._fetchCartFromBackend();
        } catch (error) {
          // If this fails, we do nothing at all
          return;
        }
      }
      if (operations.remove) {
        const listAfterDeletion = [];
        newList.forEach((item) => {
          if (!operations.remove.includes(item.product.id)) {
            listAfterDeletion.push(item);
          } else {
            // eslint-disable-next-line no-lonely-if, radix
            if (item.product.id !== DEBIT_CARD_PRODUCT_ID) {
              initialLastDeletions.push({
                item: {
                  product: item.product,
                  amount: item.serviceAmount || item.amount,
                  serviceAmount: item.serviceAmount,
                  state: item.state,
                },
                serviceVersion: !!item.serviceAmount,
                // If the product is not included on the list of favorites, then
                // if the user undoes this action, the item must be removed from favorites.
                removeFromFavorites: !!options.deleteFromFavorites,
              });
            }
          }
        });
        newList = listAfterDeletion;
      }
      if (operations.add) {
        let additionMade = false;
        operations.add.forEach((item) => {
          if (!newList.find(currentItem => item.product.id === currentItem.product.id)) {
            if (
              item.amount <= 0 ||
              item.product.maxAmountToSell < item.amount
            ) {
              return;
            }
            // Make installation amount checks
            // Installation amount must be zero if the product doesn't
            // support them. In case it does, installation amount can't be
            // greater than the amount of that product in cart.
            if (
              (item.serviceAmount && (
                item.serviceAmount < 0 ||
                !item.product.serviceProduct ||
                item.serviceAmount > item.amount
              ))
            ) {
              return;
            }
            newList.push({
              ...item,
              // Needed when adding items without state
              state: item.state || CART_ITEM_STATES.NO_ISSUES,
            });
            additionMade = true;
          }
        });
        if (additionMade && updateLastProduct) {
          this._updateLastProductUpdated(
            operations.add[0].product,
            !!operations.add[0].serviceAmount,
          );
        }
      }
      if (operations.updateAmount) {
        newList.forEach((item) => {
          const updateInfo = operations.updateAmount.find(data => data.id === item.product.id);
          // The new amount must be a positive integer smaller
          // than the max amount of this product that can
          // be sold
          if (
            !updateInfo ||
            updateInfo.amount <= 0 ||
            item.product.maxAmountToSell < updateInfo.amount
          ) {
            return;
          }
          // Make installation amount checks
          // Installation amount must be zero if the product doesn't
          // support them. In case it does, installation amount can't be
          // greater than the amount of that product in cart.
          if (
            (updateInfo.serviceAmount && (
              updateInfo.serviceAmount < 0 ||
              !item.product.serviceProduct ||
              updateInfo.serviceAmount > updateInfo.amount
            ))
          ) {
            return;
          }
          const hasService = updateInfo.serviceAmount > item.serviceAmount;
          // Since we treat products with service and without service as virtually different,
          // If the user removes all installations from a cart item, or leaves only products
          // with installation, that counts as a deletion (the cart will show one less product).
          if (forceLastDeletion) {
            if (
              (item.serviceAmount && updateInfo.serviceAmount === 0) ||
              (item.amount > item.serviceAmount && updateInfo.amount === updateInfo.serviceAmount)
            ) {
              initialLastDeletions.push({
                item: {
                  product: item.product,
                  amount: item.amount - updateInfo.amount,
                  serviceAmount: item.serviceAmount - updateInfo.serviceAmount,
                  state: item.state,
                },
                serviceVersion: item.serviceAmount && updateInfo.serviceAmount === 0,
                // If the product is not included on the list of favorites, then
                // if the user undoes this action, the item must be removed from favorites.
                removeFromFavorites: !!options.deleteFromFavorites,
              });
            }
          }
          // eslint-disable-next-line no-param-reassign
          item.amount = updateInfo.amount;
          // eslint-disable-next-line no-param-reassign
          item.serviceAmount = updateInfo.serviceAmount;
          if (updateLastProduct) {
            this._updateLastProductUpdated(item.product, hasService);
          }
        });
      }
      if (!SessionStore.loggedIn) {
        // Save to storage
        Storage.secureSet(CART_KEY, newList);
      } else {
        try {
          await this._postNewCartOnBackend(newList);
        } catch (error) {
          // If this fails, we do nothing at all
          return;
        }
      }
      // See if any product on last deletions must be removed.
      initialLastDeletions = initialLastDeletions.filter((deletedItem) => {
        const cartItem = newList.find(item => item.product.id === deletedItem.item.product.id);
        return !cartItem ||
          (deletedItem.serviceVersion && cartItem.serviceAmount === 0) ||
          (!deletedItem.serviceVersion && cartItem.amount === cartItem.serviceAmount);
      });
      // If all went well, update store
      runInAction(() => {
        this.cart = newList;
        this.lastDeletions = initialLastDeletions;
      });
    } else if (listType === LIST_TYPES.FAVORITES) {
      // Before implementing operations, update the cart.
      if (!SessionStore.loggedIn) {
        newList = CartStoreClass.fetchFavoritesFromStorage();
      } else {
        try {
          newList = await this._fetchFavoritesFromBackend();
        } catch (error) {
          // If this fails, we do nothing at all
          return;
        }
      }
      if (operations.remove) {
        newList = newList.filter(item => !operations.remove.includes(item.id));
      }
      if (operations.add) {
        operations.add.forEach((item) => {
          if (!newList.find(currentItem => item.id === currentItem.id)) {
            newList.push(item);
          }
        });
      }
      if (!SessionStore.loggedIn) {
        // Save to storage
        Storage.secureSet(FAVORITES_KEY, newList);
      } else {
        try {
          await this._postNewFavoritesOnBackend(newList);
        } catch (error) {
          // If this fails, we do nothing at all
          return;
        }
      }
      // If all went well, update store
      runInAction(() => {
        this.favorites = newList;
      });
    }
    runInAction(() => {
      this.operationInProgress = false;
    });
  }

  _turnOffPopUp() {
    this.lastTimeout = null;
    runInAction(() => {
      this.lastProductUpdated = null;
    });
  }

  flushLastDeletions() {
    runInAction(() => {
      this.lastDeletions = [];
    });
  }

  addInstallationFromProduct(productId) {
    const item = this.cart.find(cartItem => cartItem.product.id === productId);
    if (item) {
      const itemUpdate = [{
        id: item.product.id,
        amount: item.amount,
        serviceAmount: item.amount,
      }];
      return this._updateList(LIST_TYPES.CART, { updateAmount: itemUpdate });
    }
    return null;
  }

  removeInstallationFromProduct(productId) {
    const item = this.cart.find(cartItem => cartItem.product.id === productId);
    if (item) {
      const itemUpdate = [{
        id: item.product.id,
        amount: item.amount,
        serviceAmount: 0,
      }];
      return this._updateList(LIST_TYPES.CART, { updateAmount: itemUpdate });
    }
    return null;
  }

  removeInstallationsFromCart() {
    const itemsToUpdate = this.cart ? this.cart.map((item) => {
      if (!item.serviceAmount) {
        return null;
      }
      return {
        id: item.product.id,
        amount: item.amount,
        serviceAmount: 0,
      };
    }).filter(Boolean) : [];
    return this._updateList(LIST_TYPES.CART, { updateAmount: itemsToUpdate });
  }

  undoDelete(productId, isServiceVersion) {
    const deletedItem = this.lastDeletions.find(elem => (
      elem.serviceVersion === isServiceVersion &&
      elem.item.product.id === productId
    ));
    if (!deletedItem) {
      return;
    }
    // Remove the item from favorites if it wasn't there before the action
    // that must be undone (e.g. moving from cart to list)
    if (deletedItem.removeFromFavorites && this.productIsInFavorites(deletedItem.item.product.id)) {
      this.deleteProductFromFavorites(deletedItem.item.product.id);
    }
    const cartItem = this.cart.find(item => item.product.id === productId);
    if (cartItem) {
      if (isServiceVersion && cartItem.serviceAmount === 0) {
        this._updateList(
          LIST_TYPES.CART,
          {
            updateAmount: [{
              id: cartItem.product.id,
              amount: cartItem.amount + deletedItem.item.serviceAmount,
              serviceAmount: cartItem.serviceAmount + deletedItem.item.serviceAmount,
            }],
          },
        );
      } else if (!isServiceVersion && cartItem.amount === cartItem.serviceAmount) {
        this._updateList(
          LIST_TYPES.CART,
          {
            updateAmount: [{
              id: cartItem.product.id,
              amount: cartItem.amount + deletedItem.item.amount,
              serviceAmount: cartItem.serviceAmount + deletedItem.item.serviceAmount,
            }],
          },
        );
      }
    } else {
      this._updateList(
        LIST_TYPES.CART,
        {
          add: [
            deletedItem.item,
          ],
        },
      );
    }
  }

  productIsInCart(productId) {
    return !!this.cart && !!this.cart.find(cartItem => cartItem.product.id === productId);
  }

  productIsInFavorites(productId) {
    return !!this.favorites && !!this.favorites.find((
      favoritesItem => favoritesItem.id === productId
    ));
  }

  deleteProductFromCart(productId) {
    this._updateList(
      LIST_TYPES.CART,
      {
        remove: [productId],
      },
      {
        updateLastProduct: true,
      },
    );
  }

  deleteProductFromFavorites(productId) {
    this._updateList(
      LIST_TYPES.FAVORITES,
      {
        remove: [productId],
      },
    );
  }

  moveProductFromCartToList(productId, hasService) {
    const item = this.cart.find(cartItem => cartItem.product.id === productId);
    if (item) {
      if (hasService && item.amount > item.serviceAmount) {
        this._updateList(
          LIST_TYPES.CART,
          {
            updateAmount: [{
              id: productId,
              amount: item.amount - item.serviceAmount,
              serviceAmount: 0,
            }],
          },
          {
            forceLastDeletion: true,
            deleteFromFavorites: !this.productIsInFavorites(productId),
          },
        );
      } else if (!hasService && item.serviceAmount) {
        this._updateList(
          LIST_TYPES.CART,
          {
            updateAmount: [{
              id: productId,
              amount: item.serviceAmount,
              serviceAmount: item.serviceAmount,
            }],
          },
          {
            forceLastDeletion: true,
            deleteFromFavorites: !this.productIsInFavorites(productId),
          },
        );
      } else {
        this._updateList(
          LIST_TYPES.CART,
          {
            remove: [productId],
          },
          {
            updateLastProduct: true,
            deleteFromFavorites: !this.productIsInFavorites(productId),
          },
        );
      }
      this._updateList(
        LIST_TYPES.FAVORITES,
        {
          add: [item.product],
        },
      );
    }
  }

  moveProductFromListToCart(productId) {
    const item = this.favorites.find(listItem => listItem.id === productId);
    if (item) {
      this._updateList(
        LIST_TYPES.FAVORITES,
        {
          remove: [item.id],
        },
      );
      this._updateList(
        LIST_TYPES.CART,
        {
          add: [{
            product: item,
            amount: 1,
            serviceAmount: 0,
            state: CART_ITEM_STATES.NO_ISSUES,
          }],
        },
        {
          updateLastProduct: true,
        },
      );
    }
  }

  changeCartProductAmount(
    productId,
    amount,
    serviceAmount = 0,
    updateLastProduct = true,
    forceLastDeletion = false,
  ) {
    this._updateList(
      LIST_TYPES.CART,
      {
        updateAmount: [{
          id: productId,
          amount,
          serviceAmount,
        }],
      },
      {
        updateLastProduct,
        forceLastDeletion,
      },
    );
  }

  addCreditTaxToCart() {
    // Copy product in case it's observed elsewhere
    let productCopy = null;
    if (this.productDebitCardTax instanceof SimpleProduct) {
      productCopy = new SimpleProduct(this.productDebitCardTax);
    } else {
      productCopy = new Product(this.productDebitCardTax);
    }
    this._updateList(
      LIST_TYPES.CART,
      {
        add: [{
          amount: 1,
          product: productCopy,
          serviceAmount: 0,
          state: CART_ITEM_STATES.NO_ISSUES,
        }],
      },
      {
        updateLastProduct: true,
      },
    );
  }

  addToCart(product, amount = 1, serviceAmount = 0) {
    // Copy product in case it's observed elsewhere
    let productCopy = null;
    if (product instanceof SimpleProduct) {
      productCopy = new SimpleProduct(product);
    } else {
      productCopy = new Product(product);
    }
    this._updateList(
      LIST_TYPES.CART,
      {
        add: [{
          amount,
          product: productCopy,
          serviceAmount,
          state: CART_ITEM_STATES.NO_ISSUES,
        }],
      },
      {
        updateLastProduct: true,
      },
    );
  }

  addToFavorites(product) {
    // Copy product in case it's observed elsewhere
    const productCopy = new Product(product);
    this._updateList(
      LIST_TYPES.FAVORITES,
      {
        add: [productCopy],
      },
    );
  }
}

const CartStore = new CartStoreClass();

export { CartStore };



// WEBPACK FOOTER //
// ./src/stores/CartStore.js